import type { DefectFeaturesResponseBody } from '../contracts/defectFeatures';
import type { GeoJsonGeometry, GeoJsonPosition } from '../geojsonBase';

const WEB_MERCATOR_RADIUS = 6378137;
const MAX_LATITUDE = 85.05112878;

function clampLatitude(lat: number): number {
  return Math.max(-MAX_LATITUDE, Math.min(MAX_LATITUDE, lat));
}

export function wgs84ToWebMercator(
  lon: number,
  lat: number,
): readonly [x: number, y: number] {
  const clampedLat = clampLatitude(lat);
  const x = (lon * Math.PI * WEB_MERCATOR_RADIUS) / 180;
  const y =
    Math.log(Math.tan(Math.PI / 4 + (clampedLat * Math.PI) / 360)) *
    WEB_MERCATOR_RADIUS;
  return [x, y];
}

export function webMercatorToWgs84(
  x: number,
  y: number,
): readonly [lon: number, lat: number] {
  const lon = (x * 180) / (Math.PI * WEB_MERCATOR_RADIUS);
  const lat =
    (2 * Math.atan(Math.exp(y / WEB_MERCATOR_RADIUS)) - Math.PI / 2) * (180 / Math.PI);
  return [lon, lat];
}

function asLonLat(position: GeoJsonPosition): readonly [number, number] | null {
  const lon = position[0];
  const lat = position[1];
  if (typeof lon !== 'number' || typeof lat !== 'number') {
    return null;
  }
  if (!Number.isFinite(lon) || !Number.isFinite(lat)) {
    return null;
  }
  return [lon, lat];
}

function offsetRingMetres(
  ring: readonly GeoJsonPosition[],
  offsetMetres: number,
): GeoJsonPosition[] {
  if (ring.length === 0 || offsetMetres === 0) {
    return ring.map((position) => [...position]);
  }

  const mercator = ring.map((position) => {
    const lonLat = asLonLat(position);
    return lonLat ? wgs84ToWebMercator(lonLat[0], lonLat[1]) : null;
  });

  return mercator.map((point, index) => {
    const original = ring[index];
    if (!point || !original) {
      return original ? [...original] : [0, 0];
    }

    const prev = mercator[Math.max(0, index - 1)] ?? point;
    const next = mercator[Math.min(mercator.length - 1, index + 1)] ?? point;
    if (!prev || !next) {
      return [...original];
    }

    const dx = next[0] - prev[0];
    const dy = next[1] - prev[1];
    const length = Math.hypot(dx, dy);
    if (length < 1e-9) {
      return [...original];
    }

    const nx = -dy / length;
    const ny = dx / length;
    const [lon, lat] = webMercatorToWgs84(
      point[0] + nx * offsetMetres,
      point[1] + ny * offsetMetres,
    );
    return [lon, lat, ...original.slice(2)];
  });
}

export function offsetGeoJsonGeometryMetres(
  geometry: GeoJsonGeometry,
  offsetMetres: number,
): GeoJsonGeometry {
  if (offsetMetres === 0) {
    return geometry;
  }

  switch (geometry.type) {
    case 'LineString':
      return {
        type: 'LineString',
        coordinates: offsetRingMetres(geometry.coordinates, offsetMetres),
      };
    case 'MultiLineString':
      return {
        type: 'MultiLineString',
        coordinates: geometry.coordinates.map((line) =>
          offsetRingMetres(line, offsetMetres),
        ),
      };
    case 'Point':
    case 'MultiPoint':
    case 'Polygon':
    case 'GeometryCollection':
      return geometry;
    default: {
      const unreachable: never = geometry;
      return unreachable;
    }
  }
}

export function offsetDefectFeaturesMetres(
  collection: DefectFeaturesResponseBody,
  offsetMetres: number,
): DefectFeaturesResponseBody {
  if (offsetMetres === 0) {
    return collection;
  }

  return {
    type: 'FeatureCollection',
    features: collection.features.map((feature) => ({
      ...feature,
      geometry: offsetGeoJsonGeometryMetres(feature.geometry, offsetMetres),
    })),
  };
}
